import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef, useCallback, useContext } from 'react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import noop from '@atlaskit/ds-lib/noop';
import InteractionContext from '@atlaskit/interaction-context';
import { xcss } from '../xcss/xcss';
import Box from './box';
// TODO: Duplicated FocusRing styles due to lack of `xcss` support
// and to prevent additional dependency
const baseFocusRingStyles = {
  outlineColor: 'color.border.focused',
  outlineWidth: 'border.width.outline',
  outlineStyle: 'solid',
  outlineOffset: 'space.025'
};
const focusRingStyles = xcss({
  // Focus styles used when :focus-visible isn't supported
  ':focus': baseFocusRingStyles,
  // Remove default focus styles for mouse interactions if :focus-visible is supported
  ':focus:not(:focus-visible)': {
    outline: 'none'
  },
  ':focus-visible': baseFocusRingStyles,
  '@media screen and (forced-colors: active), screen and (-ms-high-contrast: active)': {
    ':focus-visible': {
      outline: '1px solid'
    }
  }
});

/**
 * __Pressable__
 *
 * A primitive for building custom buttons.
 *
 * - [Examples](https://atlassian.design/components/primitives/pressable/examples)
 * - [Code](https://atlassian.design/components/primitives/pressable/code)
 * - [Usage](https://atlassian.design/components/primitives/pressable/usage)
 */
const Pressable = /*#__PURE__*/forwardRef(({
  children,
  backgroundColor,
  padding,
  paddingBlock,
  paddingBlockStart,
  paddingBlockEnd,
  paddingInline,
  paddingInlineStart,
  paddingInlineEnd,
  isDisabled,
  type = 'button',
  testId,
  xcss: xcssStyles,
  onClick: providedOnClick = noop,
  interactionName,
  componentName,
  analyticsContext,
  ...htmlAttributes
}, ref) => {
  const interactionContext = useContext(InteractionContext);
  const handleClick = useCallback((e, analyticsEvent) => {
    interactionContext && interactionContext.tracePress(interactionName, e.timeStamp);
    providedOnClick(e, analyticsEvent);
  }, [providedOnClick, interactionContext, interactionName]);
  const onClick = usePlatformLeafEventHandler({
    fn: handleClick,
    action: 'clicked',
    componentName: componentName || 'Pressable',
    packageName: "@atlaskit/primitives",
    packageVersion: "8.0.0",
    analyticsData: analyticsContext,
    actionSubject: 'button'
  });

  // Combine default styles with supplied styles. XCSS does not support deep nested arrays
  let styles = [xcss({
    cursor: isDisabled ? 'not-allowed' : 'pointer'
  }), focusRingStyles];

  // We're type coercing this as Compiled styles in an array isn't supported by the types
  // But the runtime accepts it none-the-wiser. We can remove this entire block and replace
  // it with cx(defaultStyles, focusRingStyles, xcssStyles) when we've moved away from Emotion.
  styles = Array.isArray(xcssStyles) ? [...styles, ...xcssStyles] : [...styles, xcssStyles];
  return /*#__PURE__*/React.createElement(Box, _extends({}, htmlAttributes, {
    // @ts-expect-error - `as` is not compatible with Box. Pressable will be rewritten to diverge from Box soon.
    as: "button",
    ref: ref,
    testId: testId,
    type: type,
    onClick: onClick,
    backgroundColor: backgroundColor,
    padding: padding,
    paddingBlock: paddingBlock,
    paddingBlockStart: paddingBlockStart,
    paddingBlockEnd: paddingBlockEnd,
    paddingInline: paddingInline,
    paddingInlineStart: paddingInlineStart,
    paddingInlineEnd: paddingInlineEnd
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    ,
    xcss: styles,
    disabled: isDisabled
  }), children);
});
export default Pressable;